import { format } from 'date-fns';
import { formatCurrencyDecimals, formatNumber } from 'utils/formats';
import { Society } from 'types/Society';
import { TradingOfferOperation } from 'types/TradingOfferOperation';

type ContractTagsParams = {
  society: Society;
  operation: TradingOfferOperation;
  operationReference: string;
};

/**
 * Returns tag groups compatible with `updateDocumentWithValues` for the
 * equity market share purchase contract template.
 *
 * Variable names are aligned with the default template stored by
 * `equityMarketContractTemplateService` on the API side:
 *   {{ciudad}}, {{fecha}}, {{hora}}, {{nombre_vendedor}}, {{dni_vendedor}},
 *   {{nombre_comprador}}, {{dni_comprador}}, {{num_acciones}},
 *   {{clase_acciones}}, {{nombre_sociedad}}, {{cif_sociedad}},
 *   {{precio_accion}}, {{precio_total}}, {{referencia_operacion}}
 */
const getTradingOfferContractTags = ({
  society,
  operation,
  operationReference,
}: ContractTagsParams) => {
  const now = new Date();

  const buyer =
    operation.buyer && typeof operation.buyer !== 'string'
      ? operation.buyer
      : null;
  const seller =
    operation.seller && typeof operation.seller !== 'string'
      ? operation.seller
      : null;
  const shareClass =
    operation.shareClass && typeof operation.shareClass !== 'string'
      ? operation.shareClass
      : null;

  const contractTags = {
    title: 'Contrato Mercado Acciones',
    menu: [
      {
        title: 'ciudad',
        value: 'ciudad',
        url: society?.legalAddress?.city || '',
      },
      {
        title: 'fecha',
        value: 'fecha',
        url: format(now, 'dd/MM/yyyy'),
      },
      {
        title: 'hora',
        value: 'hora',
        url: format(now, 'HH:mm'),
      },
      {
        title: 'nombre_vendedor',
        value: 'nombre_vendedor',
        url: seller?.name || '',
      },
      {
        title: 'dni_vendedor',
        value: 'dni_vendedor',
        url: (seller as any)?.cif || '',
      },
      {
        title: 'nombre_comprador',
        value: 'nombre_comprador',
        url: buyer?.name || '',
      },
      {
        title: 'dni_comprador',
        value: 'dni_comprador',
        url: (buyer as any)?.cif || '',
      },
      {
        title: 'num_acciones',
        value: 'num_acciones',
        url: formatNumber(operation.sharesCount),
      },
      {
        title: 'clase_acciones',
        value: 'clase_acciones',
        url: (shareClass as any)?.name || '',
      },
      {
        title: 'nombre_sociedad',
        value: 'nombre_sociedad',
        url: society?.name || '',
      },
      {
        title: 'cif_sociedad',
        value: 'cif_sociedad',
        url: society?.cif || '',
      },
      {
        title: 'precio_accion',
        value: 'precio_accion',
        url: formatCurrencyDecimals(operation.sharePrice),
      },
      {
        title: 'precio_total',
        value: 'precio_total',
        url: formatCurrencyDecimals(operation.totalPrice),
      },
      {
        title: 'referencia_operacion',
        value: 'referencia_operacion',
        url: operationReference,
      },
    ],
  };

  return [contractTags];
};

export default getTradingOfferContractTags;
